import { useEffect, useRef, useCallback, useState } from 'react';
import type { WebSocketCheckMessage } from '../types';

interface UseMonitorWebSocketProps {
  accessToken: string | null;
  enabled: boolean;
  onCheckResult: (message: WebSocketCheckMessage) => void;
}

export function useMonitorWebSocket({ accessToken, enabled, onCheckResult }: UseMonitorWebSocketProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectDelay = 30000; // 30 seconds max
  const [isConnected, setIsConnected] = useState(false);

  // Track recent failures to group notifications
  const recentFailuresRef = useRef<WebSocketCheckMessage[]>([]);
  const notificationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Request notification permission on mount
  useEffect(() => {
    if (enabled && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {
        // User denied or error, silently continue
      });
    }
  }, [enabled]);

  const showBrowserNotification = useCallback((failures: WebSocketCheckMessage[]) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }

    let title = 'Monitor de Sites';
    let body: string;

    if (failures.length === 1) {
      body = `${failures[0].siteName} está fora do ar`;
    } else {
      const names = failures.map(f => f.siteName).join(', ');
      body = `${failures.length} sites estão fora do ar: ${names}`;
    }

    new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'monitor-failures', // Replace previous notification
      requireInteraction: false
    });
  }, []);

  const handleCheckResult = useCallback((message: WebSocketCheckMessage) => {
    // Call the parent callback
    onCheckResult(message);

    // Add to recent failures for grouping and notifications
    if (message.status === 'CRITICAL') {
      // Add to recent failures for grouping
      recentFailuresRef.current.push(message);

      // Clear existing timeout
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }

      // Set new timeout to show grouped notification after 2 seconds
      notificationTimeoutRef.current = setTimeout(() => {
        if (recentFailuresRef.current.length > 0) {
          showBrowserNotification(recentFailuresRef.current);
          recentFailuresRef.current = [];
        }
      }, 2000);
    }
  }, [onCheckResult, showBrowserNotification]);

  const connect = useCallback(() => {
    if (!accessToken || !enabled) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      // Extract base URL and convert to WebSocket protocol
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/v1';
      const wsUrl = apiUrl
        .replace('http://', 'ws://')
        .replace('https://', 'wss://')
        .replace(/\/v1$/, '');

      const ws = new WebSocket(`${wsUrl}/v1/monitor/live?token=${encodeURIComponent(accessToken)}`);

      ws.onopen = () => {
        console.log('[Monitor WebSocket] Connected');
        setIsConnected(true);
        reconnectAttempts.current = 0; // Reset on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketCheckMessage;
          if (message.type === 'CHECK_RESULT') {
            handleCheckResult(message);
          }
        } catch (err) {
          console.error('[Monitor WebSocket] Failed to parse message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('[Monitor WebSocket] Error:', error);
      };

      ws.onclose = () => {
        console.log('[Monitor WebSocket] Disconnected');
        setIsConnected(false);
        wsRef.current = null;

        // Auto-reconnect with exponential backoff
        if (enabled) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            maxReconnectDelay
          );
          reconnectAttempts.current += 1;

          console.log(`[Monitor WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[Monitor WebSocket] Connection failed:', err);
    }
  }, [accessToken, enabled, handleCheckResult]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Connect/disconnect based on enabled flag
  useEffect(() => {
    if (enabled && accessToken) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, accessToken, connect, disconnect]);

  return {
    isConnected
  };
}
